import type {
  Flow_AbstractComponent,
  Flow_ComponentProps,
} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';

import classify from 'src/utils/classify';

import css from './button.css';


export type LegacyButtonProps = Readonly<{
  type?: 'default' | 'primary' | 'destructive' | 'text';
  size?: 'large' | 'medium' | 'small';
  multi?: boolean;
  round?: boolean;
  icon?: React.ReactNode;
  disabled?: boolean | null;
  styleDisabled?: boolean | null;
  className?: string;
  children?: React.ReactNode;
  withMinWidth?: boolean;
  color?: string;
  style?: React.CSSProperties;
  onClick?: ((event: React.SyntheticEvent<HTMLElement>) => void) | null;
  onKeyPress?: ((event: React.KeyboardEvent<HTMLElement>) => void) | null;
  title?: string;
}>;

type UnstyledButtonProps = LegacyButtonProps &
  Omit<
    React.HTMLAttributes<HTMLDivElement>,
    keyof LegacyButtonProps | 'onClick'
  >;

/**
 * Legacy clickable surface (div + role="button"). Prefer design-system
 * UnstyledButton for new code.
 */
export const UnstyledButton: Flow_AbstractComponent<
  UnstyledButtonProps,
  HTMLElement
> = React.forwardRef<HTMLElement, UnstyledButtonProps>(
  ({disabled, onKeyPress, onClick, className, ...props}, ref) => (
    <div
      {...props}
      className={className}
      ref={ref as React.Ref<HTMLDivElement>}
      role="button"
      aria-disabled={disabled ? true : undefined}
      tabIndex={disabled ? undefined : 0}
      onKeyPress={(event) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onKeyPress?.(event);
        if (
          onClick &&
          !event.defaultPrevented &&
          (event.key === 'Enter' || event.key === ' ')
        ) {
          onClick(event);
        }
      }}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();
        } else {
          onClick?.(event);
        }
      }}
    />
  ),
);

UnstyledButton.displayName = 'UnstyledButton';

export const Button: Flow_AbstractComponent<LegacyButtonProps, HTMLElement> =
  React.forwardRef<HTMLElement, LegacyButtonProps>(
    (
      {
        type = 'default',
        size = 'large',
        multi,
        round,
        icon,
        disabled,
        styleDisabled,
        className,
        children,
        withMinWidth,
        color,
        style,
        ...props
      },
      ref,
    ) => (
      <UnstyledButton
        {...props}
        className={classify(className, css.buttonText, css[type], css[size], {
          [css.buttonIcon]: Boolean(icon),
          [css.buttonEnabled]: !disabled,
          [css.buttonDisabled]:
            styleDisabled != null ? styleDisabled : disabled,
          [css.buttonRound]: round,
          [css.multi]: multi,
          [css.withMinWidth]: withMinWidth,
        })}
        disabled={disabled}
        ref={ref}
        style={{
          ...style,
          ...(color && !disabled
            ? type === 'primary'
              ? {backgroundColor: color}
              : {color}
            : null),
        }}
      >
        {icon && <div className={css.icon}>{icon}</div>}
        <span className={css.children}>{children}</span>
      </UnstyledButton>
    ),
  );

Button.displayName = 'Button';

export const IconButton: Flow_AbstractComponent<
  LegacyButtonProps,
  HTMLElement
> = React.forwardRef<HTMLElement, LegacyButtonProps>(
  (
    {
      type = 'default',
      size = 'small',
      round,
      disabled,
      styleDisabled,
      className,
      ...props
    },
    ref,
  ) => (
    <UnstyledButton
      {...props}
      className={classify(className, css.iconButton, css[type], css[size], {
        [css.buttonEnabled]: !disabled,
        [css.buttonDisabled]: styleDisabled != null ? styleDisabled : disabled,
        [css.buttonRound]: round,
      })}
      ref={ref}
    />
  ),
);

IconButton.displayName = 'IconButton';

export default Button;

/** @deprecated Use LegacyButtonProps */
export type Props = LegacyButtonProps;
